import {createContext, useState, useContext, ReactNode, useEffect} from "react";
import useGetUserProfile from "../hooks/useGetUserProfile";
import {jwtDecode} from "jwt-decode";

type AppContextType = {
  token: string | null;
  setToken: (token: string | null) => void;
  isLoggedIn: boolean;
  loading: boolean;
};

type JwtPayload = {
  exp: number;
  [key: string]: any;
};

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({children}: {children: ReactNode}) => {
  const {refetch} = useGetUserProfile();
  const [token, setTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Decode JWT safely
  const decodeToken = (token: string): JwtPayload | null => {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch (err) {
      console.error("Failed to decode token", err);
      return null;
    }
  };

  // Check if token is expired
  const isTokenExpired = (token: string): boolean => {
    const payload = decodeToken(token);
    if (!payload?.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  };

  // Set token and store in localStorage
  const setToken = (newToken: string | null) => {
    if (!newToken) {
      localStorage.removeItem("iris-accessToken");
      setTokenState(null);
      return;
    }
    setTokenState(newToken);
    localStorage.setItem("iris-accessToken", newToken);
  };

  // On app mount, load token from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem("iris-accessToken");
    if (savedToken && !isTokenExpired(savedToken)) {
      setTokenState(savedToken);
    } else {
      localStorage.removeItem("iris-accessToken");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token) {
      refetch()
        .then((res) => {})
        .catch((err) => {});
    }
  }, [token]);

  return (
    <AppContext.Provider
      value={{
        token,
        setToken,
        isLoggedIn: !!token && !isTokenExpired(token),
        loading,
      }}>
      {!loading && children}
    </AppContext.Provider>
  );
};

// Hook to use AppContext
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context)
    throw new Error("useAppContext must be used within an AppProvider");
  return context;
};
